import React, { useState } from "react";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import Card from "components/Card";
import DataTable from "components/DataTable";
import {
  useAIRoleBuilderTasks,
  useAIRoleBuilderTasksContext,
  useAuth,
  useMissionRoles,
  useRoleBuilderSubmit,
} from "hooks";
import { MISSION_ROLE_STATES } from "constants/index";
import getTalentSimpleDraftColumns from "constants/talentSimpleDraftColumns";
import RoleBuilderModal from "./RoleBuilder/RoleBuilderModal";

/**
 * TalentSimpleDraftsList
 *
 * @param {Object}  filters       Board filters, including this section's states
 * @param {Object}  emptyMessage  Shown when there are neither requests nor drafts
 */
const TalentSimpleDraftsList = ({ filters = {}, emptyMessage }) => {
  const intl = useIntl();
  const { organization } = useAuth();
  const { tasks, loading: tasksLoading, missionId } = useAIRoleBuilderTasksContext();
  const { handleDiscard } = useAIRoleBuilderTasks();
  const {
    data: roles = [],
    loading: rolesLoading,
    deleting,
    handleRemoveRoles,
  } = useMissionRoles({
    filters,
    disablePagination: true,
    includeCustomerBudget: true,
  });
  const { submitRole } = useRoleBuilderSubmit();
  const [editing, setEditing] = useState(null);
  const [removingRoleIds, setRemovingRoleIds] = useState([]);

  const removeDraftRole = async (role) => {
    setRemovingRoleIds((ids) => [...ids, role.id]);
    await handleRemoveRoles([role.id], false, role);
    setRemovingRoleIds((ids) => ids.filter((id) => id !== role.id));
  };

  // Requests and roles come from different tables, so their ids are prefixed to stay unique as row keys.
  const rows = [
    ...filterTasks(tasks, filters).map((task) => ({ id: `task-${task.id}`, task })),
    ...roles.map((role) => ({ id: `role-${role.id}`, role })),
  ];

  return (
    <>
      <Card>
        <DataTable
          data={rows}
          columns={getTalentSimpleDraftColumns({
            intl,
            onEdit: setEditing,
            onDiscardAIRequest: handleDiscard,
            onRemoveDraftRole: removeDraftRole,
            onOpenDraftRole: submitRole,
            removingRoleIds: deleting ? removingRoleIds : [],
          })}
          loading={tasksLoading || rolesLoading}
          emptyMessage={emptyMessage}
          hideHeader
        />
      </Card>
      {editing && (
        <RoleBuilderModal
          show
          onClose={() => setEditing(null)}
          task={editing.task}
          role={editing.role}
          missionId={editing.role?.mission?.uuid || missionId}
          companyName={organization?.name}
          companyUrl={organization?.url}
        />
      )}
    </>
  );
};

// Requests have no server-side filtering, so the board's keyword and status filters are applied here.
const filterTasks = (tasks, { keyword, state }) => {
  if (state && state !== MISSION_ROLE_STATES.draftInProgress) return [];

  const search = keyword?.trim().toLowerCase();

  if (!search) return tasks;

  return tasks.filter((task) =>
    [task.label, task.companyName, task.jobDescription].some((text) => text?.toLowerCase().includes(search))
  );
};

TalentSimpleDraftsList.propTypes = {
  filters: PropTypes.object,
  emptyMessage: PropTypes.object,
};

export default TalentSimpleDraftsList;
